/**
 * Shrink a photo before it goes up.
 *
 * A modern phone camera writes 3–5 MB per shot. On a truck's signal that is a
 * slow, failure-prone upload, and it fills the photo disk fast. 1600px on the
 * long edge at 80% quality is still plenty to show a finished yard, and lands
 * around 300–600 KB.
 *
 * Anything unexpected — an odd format, a browser without canvas, a HEIC the
 * browser cannot decode — returns the original file untouched. Shrinking is an
 * optimisation; never let it lose a crew member's photo.
 */
const MAX_EDGE = 1600;
const QUALITY = 0.8;
const SKIP_BELOW = 400 * 1024;  // already small — don't re-encode

export async function shrinkPhoto(file: File): Promise<File> {
  try {
    if (!file.type.startsWith('image/') || file.size <= SKIP_BELOW) return file;
    if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;

    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size <= 2 * 1024 * 1024) { bitmap.close?.(); return file; }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) { bitmap.close?.(); return file; }
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();

    const blob: Blob | null = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
    if (!blob || blob.size >= file.size) return file;    // no gain — keep the original

    const name = file.name.replace(/\.(heic|heif|png|webp|jpeg|jpg)$/i, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}
